<template>
  <div class="filter-select">
    <BMenu placement="bottom-start">
      <template #activator>
        <BBtn
          flat
          size="small"
        >
          <BCustomIcon
            class="mr-50"
            icon-class="b-column"
            size="small"
          /><span>{{ $t('leadList.column.title') }}</span>
        </BBtn>
      </template>
      <BCard
        class="column-select-card"
        small
      >
        <div
          v-for="listColumn in listColumns"
          :key="listColumn"
          class="pb-200"
        >
          <div class="sub-title">
            {{ $t(`filterSelect.${listColumn}.title`) }}
          </div>
          <BDivider class="mb-100" />
          <template v-if="listColumn !== 'customField'">
            <div
              v-for="[key, message] in Object.entries($tm(`filterSelect.${listColumn}.column`))"
              :key="key"
              class="select-item"
            >
              <BCheckbox
                v-if="!isExceptColumn(key)"
                v-model="columnChecks[key]"
                class="mb-100"
                :label="$rt(message)"
                @change="() => onCheckChanged(key)"
              />
            </div>
          </template>
          <template v-else>
            <div
              v-for="customField in customFields"
              :key="customField.fieldKey"
              class="select-item"
            >
              <BCheckbox
                v-model="columnChecks[customField.fieldKey]"
                class="mb-100"
                :label="customField.name"
                @change="() => onCheckChanged(customField.fieldKey)"
              />
            </div>
          </template>
        </div>
        <div
          v-if="showFooterActions"
          class="panel-footer my-200"
        >
          <BLayout
            align-center
            justify-center
            gap="10px"
          >
            <BTooltip
              top
              :content="resetTooltipMessage"
            >
              <BBtn
                type="default"
                flat
                class="reset-btn"
                size="mini"
                @click="resetDisplayColumn"
              >
                <span class="footer-text reset">{{ $t('general.reset.text') }}</span>
              </BBtn>
            </BTooltip>
            <BBtn
              v-if="isOwnerAndAllLeadsView"
              type="primary"
              class="save-as-default-btn"
              :loading="saveAsDefaultLoading"
              size="mini"
              @click="handleSaveAsDefaultClick"
            >
              <span class="footer-text">{{ $t('leadList.saveAsDefault.title') }}</span>
            </BBtn>
          </BLayout>
        </div>
      </BCard>
    </BMenu>
  </div>
</template>

<script lang="ts">
import lodash from 'lodash';
import { useI18n } from 'vue-i18n';
import { mapWaitingActions } from 'vue-wait';
import { mapGetters } from 'vuex';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useConfirm } from '@/composable/message-dialog';

export default {
  name: 'ListColumnSelect',
  props: {
    exceptInfo: {
      type: Array as () => string[],
      required: false,
      default: () => [],
    },
    exceptColumns: {
      type: Array as () => string[],
      required: false,
      default: () => [],
    },
    hasCustomDefaultSetting: {
      type: Boolean,
      required: false,
      default: false,
    },
    showFooterActions: {
      type: Boolean,
      required: false,
      default: true,
    },
    saveAsDefaultLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['click:resetColumns', 'click:saveAsDefault'],
  setup() {
    const { isAvailableSalesforceLinkFeature } = useAvailableFeatureCheck();
    const { inject: injectCurrentUser } = useCurrentUserProvider();
    const { authorize } = injectCurrentUser();
    const { confirm } = useConfirm();
    const { t } = useI18n();
    return {
      isAvailableSalesforceLinkFeature,
      authorize,
      confirm,
      t,
    };
  },
  computed: {
    ...mapGetters('user', [
      'salesforceSetting',
      'customFields',
      'leadView',
    ]),
    ...mapGetters('userUi', [
      'leadListSelectedColumns',
    ]),
    isOwnerAndAllLeadsView() {
      return !this.leadView?.id && this.authorize(['owner']);
    },
    listColumns () {
      const listColumns =  ['companyInfo', 'callTargetInfo', 'customField', 'historyInfo', 'leadAdditionalInfo', 'lastActivityInfo', 'systemAdditionalInfo', 'duplicateInfo'];
      if (this.salesforceSetting && this.isAvailableSalesforceLinkFeature) {
        return listColumns.concat(['salesforceInfo']);
      }
      if (this.exceptInfo.length > 0) {
        return listColumns.filter(i => this.exceptInfo.indexOf(i) === -1);
      }
      return listColumns;
    },
    columnChecks () {
      // storeにすでに存在する表示項目は true にしておく
      const columnChecks = {};
      this.leadListSelectedColumns.forEach((key) => {
        columnChecks[key] = true;
      });
      return columnChecks;
    },
    resetTooltipMessage() {
      return this.hasCustomDefaultSetting
        ? this.t('leadList.reset.tooltip.set')
        : this.t('leadList.reset.tooltip.notSet');
    },
  },
  methods: {
    ...mapWaitingActions('userUi', {
      setLeadListSelectedColumnsAction: 'setLeadListSelectedColumnsWait',
    }),
    onCheckChanged (key) {
      const leadListSelectedColumns = lodash.cloneDeep(this.leadListSelectedColumns);
      const index = leadListSelectedColumns.indexOf(key);
      const shouldAdding = index === -1;
      // leadListSelectedColumns に存在しない場合は追加、既に存在する場合は削除する
      if (shouldAdding) {
        leadListSelectedColumns.push(key);
      } else {
        leadListSelectedColumns.splice(index, 1);
      }
      this.setLeadListSelectedColumnsAction(leadListSelectedColumns);
    },
    resetDisplayColumn () {
      this.$emit('click:resetColumns');
    },
    isExceptColumn(key) {
      return this.exceptColumns.indexOf(key) !== -1;
    },
    async handleSaveAsDefaultClick() {
      const isConfirmed = await this.confirm({
        message: this.t('leadList.saveAsDefault.confirm'),
      });
      if (!isConfirmed) return;

      this.$emit('click:saveAsDefault', this.leadListSelectedColumns);
    },
  },
};
</script>

<style lang="scss" scoped>
  .column-select-card {
    max-height: 550px;
    @include m-fixed-width(320px);
    overflow: auto;
  }

  .sub-title {
    font-size: 12px;
    color: $textcolor-light;
    padding-bottom: $basespace-100;
  }

  .reset-btn {
    color: $concrete-dark;
  }
</style>
